import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cell } from 'styled-css-grid';

import { FormField, xcriticalFormPropertyChange } from '@xcritical/forms';

import { Label } from '@crm-framework/label';
import { useRegistry } from '@crm-framework/registry';
import { CheckboxFormField } from '@crm-framework/form-fields';

import { DictionarySelect, useDictionary } from '@crm-package/dictionaries';

import { ValueWrapper } from '../styled';
import {
  CONVERSION_STATUS,
  DICTIONARY_EMPTY_VALUE_ID,
  IS_QUALIFIED,
  LeadDetailsDictionaries,
  LEAD_FORM_NAME,
  LEAD_REGISTRY,
  REJECT_REASON_FOR_NOT_VALID,
  REJECT_REASON_FOR_NO_POTENTIAL,
  STATUS_FULLY_ACTIVATED,
  STATUS_NEW,
  STATUS_NOT_VALID,
  STATUS_NO_POTENTIAL,
  TRADING_STATUS,
  CUSTOM_EMPTY_VALUE,
} from '../consts';
import { getStatusBlockValues } from '../selectors';

export const StatusesBlock: React.FC = () => {
  const dispatch = useDispatch();
  const salesStatusDictionary = useDictionary(
    LeadDetailsDictionaries.availableSalesStatuses
  );
  const {
    rejectReasonDictionaryName,
    isDisabledRejectReasonField,
    isDisabledTradingStatusField,
  } = useSelector(getStatusBlockValues);

  const isMainFormLoading = useRegistry<boolean>(
    LEAD_REGISTRY.mainFormLoading,
    false
  );

  const onChangeIsQualified = useCallback(
    (value: boolean) => {
      const newConversionStatusItem = salesStatusDictionary.find(
        (item) => item.name === (value ? STATUS_NEW : STATUS_NOT_VALID)
      );

      const newConversionStatusId = newConversionStatusItem?.disabled
        ? undefined
        : newConversionStatusItem?.id;
      dispatch(
        xcriticalFormPropertyChange(
          LEAD_FORM_NAME,
          CONVERSION_STATUS,
          newConversionStatusId
        )
      );
      dispatch(
        xcriticalFormPropertyChange(
          LEAD_FORM_NAME,
          REJECT_REASON_FOR_NO_POTENTIAL,
          DICTIONARY_EMPTY_VALUE_ID
        )
      );
      dispatch(
        xcriticalFormPropertyChange(
          LEAD_FORM_NAME,
          REJECT_REASON_FOR_NOT_VALID,
          DICTIONARY_EMPTY_VALUE_ID
        )
      );
      dispatch(
        xcriticalFormPropertyChange(
          LEAD_FORM_NAME,
          TRADING_STATUS,
          DICTIONARY_EMPTY_VALUE_ID
        )
      );
    },
    [salesStatusDictionary]
  );

  const onSalesStatusChange = useCallback(
    (id: number) => {
      const newStatus = salesStatusDictionary.find((item) => item.id === id);

      switch (newStatus?.name) {
        case STATUS_NOT_VALID: {
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              TRADING_STATUS,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NO_POTENTIAL,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NOT_VALID,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, IS_QUALIFIED, false)
          );

          return;
        }
        case STATUS_FULLY_ACTIVATED: {
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, TRADING_STATUS, 0)
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NO_POTENTIAL,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NOT_VALID,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, IS_QUALIFIED, true)
          );

          return;
        }
        case STATUS_NO_POTENTIAL: {
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, TRADING_STATUS, 0)
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NO_POTENTIAL,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NOT_VALID,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, IS_QUALIFIED, true)
          );

          return;
        }
        default: {
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, TRADING_STATUS, 0)
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NO_POTENTIAL,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(
              LEAD_FORM_NAME,
              REJECT_REASON_FOR_NOT_VALID,
              DICTIONARY_EMPTY_VALUE_ID
            )
          );
          dispatch(
            xcriticalFormPropertyChange(LEAD_FORM_NAME, IS_QUALIFIED, true)
          );
        }
      }
    },
    [salesStatusDictionary]
  );

  return (
    <>
      <Cell>
        <Label isRequired htmlFor={CONVERSION_STATUS}>
          Conversion Status
        </Label>
      </Cell>
      <FormField
        inputId={CONVERSION_STATUS}
        name={CONVERSION_STATUS}
        component={DictionarySelect}
        dictionaryName={LeadDetailsDictionaries.availableSalesStatuses}
        shouldFitContainer
        openMenuOnFocus
        menuPlacement="auto"
        hideSelectedOptions={false}
        isSearchable
        appearance="compact"
        disabled={isMainFormLoading}
        onChange={onSalesStatusChange}
      />
      <Label>Is Qualified</Label>
      <ValueWrapper>
        <FormField
          name={IS_QUALIFIED}
          component={CheckboxFormField}
          disabled={isMainFormLoading}
          onChange={onChangeIsQualified}
        />
      </ValueWrapper>
      <Label htmlFor="rejectReason">Reject Reason</Label>
      <FormField
        inputId="rejectReason"
        disabled={isDisabledRejectReasonField || isMainFormLoading}
        name={`${rejectReasonDictionaryName}Id`}
        component={DictionarySelect}
        dictionaryName={rejectReasonDictionaryName}
        shouldFitContainer
        openMenuOnFocus
        menuPlacement="auto"
        hideSelectedOptions={false}
        isSearchable
        appearance="compact"
      />
      <Label htmlFor={TRADING_STATUS}>Trading Status</Label>
      <FormField
        inputId={TRADING_STATUS}
        name={TRADING_STATUS}
        component={DictionarySelect}
        dictionaryName={LeadDetailsDictionaries.tradingStatus}
        disabled={isDisabledTradingStatusField || isMainFormLoading}
        customEmptyValue={CUSTOM_EMPTY_VALUE}
        shouldFitContainer
        openMenuOnFocus
        menuPlacement="auto"
        hideSelectedOptions={false}
        isSearchable
        appearance="compact"
      />
    </>
  );
};
